import { useEffect, useRef } from 'react'

import { useDetailId } from './use-query-states'

/**
 * AHK's `Ctrl+L → paste → Enter` scan flow rewrites the URL to whatever the QR
 * encoded — the scanner navigates to `?skid=<label>` and the React app reboots.
 * Most preserved query params survive that flip (search, ship-date — the QR is
 * generated with them baked in), but `?detail=` does not. The operator was
 * looking at a specific detail panel right before scanning; after the flip
 * they're dumped back to the orders board with no detail open and have to
 * scroll/click to find their place again.
 *
 * Mirror the pattern in `src/store/skid-panel.ts`: stash the active param in
 * sessionStorage with a wall-clock timestamp, restore it on mount when the URL
 * is missing the value AND the stored entry is fresh. Same TTL so persisted
 * sessions from a prior task can't pin the panel open indefinitely.
 *
 * Explicit close (user clears `?detail=`) drops the stored entry — that signals
 * "no detail wanted" and shouldn't be reopened by the next scan reload.
 */

// Match `PERSISTED_SCAN_TTL_MS` in `src/store/skid-panel.ts` — `?detail=`
// rehydrates alongside bulkScan/bundleScanActive after an AHK reload, and
// stale state from a prior task should expire on the same schedule.
const TTL_MS = 60 * 1000
const PREFIX = 'pkg-persist:'

export function persistPackagingParam(key: string, value: string | null | undefined): void {
  if (typeof sessionStorage === 'undefined') return
  if (value && value.length > 0) {
    sessionStorage.setItem(
      `${PREFIX}${key}`,
      JSON.stringify({ value, at: Date.now() })
    )
  } else {
    sessionStorage.removeItem(`${PREFIX}${key}`)
  }
}

export function restorePackagingParam(
  key: string,
  now: number = Date.now(),
  ttlMs: number = TTL_MS
): string | null {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(`${PREFIX}${key}`)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { value?: unknown; at?: unknown }
    const value = typeof parsed.value === 'string' ? parsed.value : null
    const at = typeof parsed.at === 'number' ? parsed.at : 0
    if (!value || now - at > ttlMs) {
      sessionStorage.removeItem(`${PREFIX}${key}`)
      return null
    }
    return value
  } catch {
    sessionStorage.removeItem(`${PREFIX}${key}`)
    return null
  }
}

/**
 * Mount-once: rehydrate `?detail=` from sessionStorage when the URL doesn't
 * carry it (typical post-AHK-scan state). Subsequent renders write through.
 */
export function usePackagingUrlPersistence(): void {
  const [detailId, setDetailId] = useDetailId()
  const hasMounted = useRef(false)

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      if (!detailId) {
        const restored = restorePackagingParam('detail')
        if (restored) {
          setDetailId(restored)
          // Don't persist on this render — the next render with the restored
          // value will sync sessionStorage naturally.
          return
        }
      }
    }
    persistPackagingParam('detail', detailId)
  }, [detailId, setDetailId])
}
